// apps/admin/src/pages/CapabilitiesPage.jsx
//
// Staff-facing capability inventory: what is built, what is partial, what
// is stubbed, what will never be built - with a citation on every row and
// a link into the console feature itself.
//
// Blunt on purpose. This is the page that should have prevented three
// capabilities being reported as missing when they were shipping.

import { useState } from 'react';
import { ExternalLink, Search } from 'lucide-react';
import { CAPABILITIES, CAPABILITY_GROUPS, CAPABILITY_STATES } from '../lib/capabilityCatalog.js';

const CONSOLE_BASE =
  (typeof window !== 'undefined' && window.ENV && window.ENV.CONSOLE_URL) ||
  'https://console.govern360.ai';

export default function CapabilitiesPage() {
  const [q, setQ] = useState('');
  const [stateFilter, setStateFilter] = useState('all');

  const needle = q.trim().toLowerCase();
  const rows = CAPABILITIES.filter((c) => {
    if (stateFilter !== 'all' && c.state !== stateFilter) return false;
    if (!needle) return true;
    return (c.name + ' ' + c.does + ' ' + (c.limit || '') + ' ' + c.evidence.join(' '))
      .toLowerCase().includes(needle);
  });

  const counts = {};
  for (const c of CAPABILITIES) counts[c.state] = (counts[c.state] || 0) + 1;

  return (
    <div>
      <div className="ph">
        <div>
          <h1 className="ph-h1">Capabilities</h1>
          <p className="ph-sub">
            What Govern360 does, what it does not, and where to look. Every row cites a
            migration or a file, and the build fails if a citation stops resolving.
          </p>
        </div>
      </div>

      <div className="row r4" style={{ marginBottom: 16 }}>
        {Object.keys(CAPABILITY_STATES).map((k) => (
          <div className="card" key={k}>
            <p className="metric-l">{CAPABILITY_STATES[k].label}</p>
            <p className="metric-v">{counts[k] || 0}</p>
          </div>
        ))}
      </div>

      <div className="card" style={{ marginBottom: 16, display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flex: 1, minWidth: 240 }}>
          <Search size={16} style={{ color: 'var(--g-text-2)', flexShrink: 0 }} />
          <input
            className="inp"
            placeholder="Search capability, limit or citation"
            value={q}
            onChange={(e) => setQ(e.target.value)}
          />
        </div>
        <select className="inp" style={{ maxWidth: 200 }} value={stateFilter} onChange={(e) => setStateFilter(e.target.value)}>
          <option value="all">All states</option>
          {Object.keys(CAPABILITY_STATES).map((k) => (
            <option key={k} value={k}>{CAPABILITY_STATES[k].label}</option>
          ))}
        </select>
      </div>

      {CAPABILITY_GROUPS.map((g) => {
        const inGroup = rows.filter((c) => c.group === g);
        if (!inGroup.length) return null;
        return (
          <div className="card" key={g} style={{ marginBottom: 16 }}>
            <div className="card-h"><h3>{g}</h3></div>
            {inGroup.map((c) => (
              <div key={c.id} style={{ padding: '14px 0', borderBottom: '1px solid var(--g-divider)' }}>
                <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10, flexWrap: 'wrap' }}>
                  <strong style={{ fontSize: 14 }}>{c.name}</strong>
                  <span className={'pill ' + CAPABILITY_STATES[c.state].pill}>
                    {CAPABILITY_STATES[c.state].label}
                  </span>
                  {c.route && (
                    <a
                      className="btn outline"
                      style={{ height: 24, fontSize: 11, padding: '0 10px', marginLeft: 'auto' }}
                      href={CONSOLE_BASE + '/#' + c.route}
                      target="_blank"
                      rel="noreferrer"
                    >
                      <ExternalLink size={12} />Open
                    </a>
                  )}
                </div>
                <p style={{ margin: '6px 0 0', fontSize: 13, color: 'var(--g-text-2)', lineHeight: 1.55 }}>
                  {c.does}
                </p>
                {c.limit && (
                  <p style={{ margin: '6px 0 0', fontSize: 12, color: 'var(--g-warning)', lineHeight: 1.55 }}>
                    <strong>Limit:</strong> {c.limit}
                  </p>
                )}
                <div style={{ marginTop: 6, display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                  {c.evidence.map((e) => (
                    <span key={e} className="mono" style={{ fontSize: 10, color: 'var(--g-text-3)', background: 'var(--g-bg-soft)', padding: '2px 6px', borderRadius: 4 }}>
                      {e}
                    </span>
                  ))}
                </div>
              </div>
            ))}
          </div>
        );
      })}

      <div className="banner" style={{ marginTop: 8 }}>
        <span>
          A capability with no citation does not get a row. If something looks missing here,
          check the code before adding it - three capabilities were reported missing in one
          evening that were shipping, and this catalog rejected four invented citations on
          its first run.
        </span>
      </div>
    </div>
  );
}